'use client';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { blogApi } from '@/lib/api';
import { BlogPost } from '@/types';

export default function BlogListing() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    blogApi.getAll().then(data => {
      setPosts(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const placeholders: BlogPost[] = [
    { id: 1, title: 'Getting Started with Django REST Framework', slug: 'getting-started-drf', excerpt: 'Learn how to build powerful REST APIs using Django and Django REST Framework in this comprehensive guide.', cover_image: null, tags: ['Django', 'Python', 'API'], published_at: '2024-05-15T10:00:00Z', read_time: 5 },
    { id: 2, title: 'Why Next.js is the Future of Frontend', slug: 'nextjs-future', excerpt: 'Explore the features that make Next.js the go-to framework for modern React applications.', cover_image: null, tags: ['Next.js', 'React', 'Frontend'], published_at: '2024-04-20T10:00:00Z', read_time: 7 },
  ];

  const displayPosts = posts.length > 0 ? posts : placeholders;

  return (
    <div style={{ paddingTop: '8rem', minHeight: '100vh', paddingBottom: '4rem' }}>
      <div className="container">
        <Link href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', marginBottom: '2rem', fontFamily: 'Outfit, sans-serif' }}>
          <ArrowLeft size={16} /> Back to Home
        </Link>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="accent-line" />
          <h1 className="section-title">Blog</h1>
          <p className="section-subtitle">Writing about code, design, and building products.</p>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '2rem' }}>
          {displayPosts.map((post, i) => (
            <Link href={`/blog/${post.slug}`} key={post.id} style={{ textDecoration: 'none', color: 'inherit' }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="card"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div style={{ height: '180px', borderRadius: '8px', overflow: 'hidden', marginBottom: '1.25rem', background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
                  {post.cover_image ? (
                    <Image src={post.cover_image} alt={post.title} width={400} height={200} style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: '3rem', opacity: 0.5 }}>
                      ✍️
                    </div>
                  )}
                </div>
                
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                    {post.tags.map(tag => <span key={tag} className="tag" style={{ fontSize: '0.75rem' }}>{tag}</span>)}
                  </div>
                  <h2 style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.75rem', lineHeight: 1.4 }}>
                    {post.title}
                  </h2>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
                    {post.excerpt}
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--border)', paddingTop: '1rem', marginTop: 'auto', color: 'var(--text-subtle)', fontSize: '0.85rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Calendar size={14} />
                    {new Date(post.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <Clock size={14} />
                    {post.read_time} min read
                  </div>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
